/**
 * PATH: packages/ui/src/shortcuts/useShortcutOverridesRuntime.ts
 * FILE_ID: UI-SHORTCUTS-RUNTIME-OVERRIDES-001
 * PURPOSE: READ-ONLY override source that feeds <ShortcutProvider> in the app
 *          shell. Reads the same persisted slice the settings editor writes
 *          (localStorage + /api/settings/ui/'s `shortcut_overrides`) so a user's
 *          remap / disable takes effect at dispatch time.
 * WHY: @quotech/ui can't import settings-hub's useShortcutOverrides (that would
 *      be a dependency cycle — settings-hub depends on @quotech/ui). The editor
 *      owns writes; this hook only mirrors what was persisted, sharing the
 *      storage key + change-event contract from ./types so the two stay in sync.
 *
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌──────────────────────────┐
 *   │ localStorage  │ → │ initial render      │ → │ ShortcutOverrideMap        │
 *   │ (sync, coerced)│  │ then GET /settings/ │   │ re-read on same-tab edit   │
 *   └──────────────┘   │ ui/ hydrates        │   │ (event) + cross-tab storage│
 *                      └─────────────────────┘   └──────────────────────────┘
 *
 * DEPENDENCIES:
 *   - ./overrides: coerceOverrideMap (defensive validation of persisted JSON)
 *   - ./types: SHORTCUT_OVERRIDES_STORAGE_KEY, SHORTCUT_OVERRIDES_CHANGED_EVENT
 *   - @quotech/tenant-shared: getUiPreferences (backend hydration)
 */

import { useEffect, useRef, useState } from 'react'
import { getUiPreferences } from '@quotech/tenant-shared/settings/quotechSettings'
import { coerceOverrideMap } from './overrides'
import {
  SHORTCUT_OVERRIDES_CHANGED_EVENT,
  SHORTCUT_OVERRIDES_STORAGE_KEY,
  type ShortcutOverrideMap,
} from './types'

/** Synchronously read + coerce the persisted override map (SSR/storage-safe). */
function readLocal(): ShortcutOverrideMap {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(SHORTCUT_OVERRIDES_STORAGE_KEY)
    if (raw) return coerceOverrideMap(JSON.parse(raw))
  } catch {
    // localStorage unavailable / malformed JSON — fall back to defaults
  }
  return {}
}

/**
 * useShortcutOverridesRuntime — returns the effective override map for the
 * dispatcher. Initialises synchronously from localStorage (no flash), hydrates
 * once from the backend on mount (so a fresh device picks up cross-device
 * customizations), and re-reads on same-tab edits (the editor's change event)
 * and cross-tab edits (the native `storage` event). A same-tab edit guards the
 * one-shot backend hydration so a slow GET can't clobber a just-made change.
 */
export function useShortcutOverridesRuntime(): ShortcutOverrideMap {
  const [overrides, setOverrides] = useState<ShortcutOverrideMap>(readLocal)
  // Flips once a same-tab edit has been observed; prevents the late backend
  // hydration from overwriting the operator's fresh local change.
  const editedRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onLocalEdit = () => {
      editedRef.current = true
      setOverrides(readLocal())
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === SHORTCUT_OVERRIDES_STORAGE_KEY) setOverrides(readLocal())
    }
    window.addEventListener(SHORTCUT_OVERRIDES_CHANGED_EVENT, onLocalEdit)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(SHORTCUT_OVERRIDES_CHANGED_EVENT, onLocalEdit)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const prefs = await getUiPreferences()
        const remote = coerceOverrideMap(prefs?.shortcut_overrides)
        if (!cancelled && !editedRef.current) setOverrides(remote)
      } catch {
        // offline / endpoint unavailable — keep the localStorage value
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return overrides
}
